// Helper around the project-wide Fancybox v3 instance, opt-in for the
// add-house media preview. No global Fancybox defaults are mutated here.

function escapeHtmlAttr(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

// Extract the upstream photo filename from any of the URL variants the property
// pipeline emits (storage/photos/property/large/<file> for original, /img/.../<file>/W/H
// for transformed). Matching on filename rather than the full URL lets us reconcile
// promoImageUrl (listingUrl variant) with gallery records exposing fileUrl/smallUrl.
export function extractPropertyPhotoFilename(url) {
    if (typeof url !== 'string' || url.length === 0) return null;
    var clean = url.split('#')[0].split('?')[0];
    var m = clean.match(/\/large\/([^\/]+\.[a-zA-Z0-9]+)/);
    return m ? m[1] : null;
}

export function openSinglePropertyImage(url, captionText, optionsOverride) {
    if (typeof url !== 'string' || url.length === 0) return false;
    return !!openPropertyGallery([{
        src: url,
        type: 'image',
        opts: { caption: captionText || '', thumb: url },
    }], 0, optionsOverride);
}

function isPdfExtension(name) {
    return typeof name === 'string' && /\.pdf$/i.test(name);
}

function isImageExtension(name) {
    return typeof name === 'string' && /\.(jpe?g|png|gif|webp)$/i.test(name);
}

function buildPdfIframeItem(fileUrl, captionText, thumbUrl) {
    var encoded = encodeURIComponent(fileUrl);
    var viewerUrl = '/viewer/web/index.html?file=' + encoded;
    return {
        src: viewerUrl,
        type: 'iframe',
        opts: {
            caption: captionText || '',
            thumb: thumbUrl || null,
            iframe: { preload: false },
            width: '90vw',
            height: '90vh',
        },
    };
}

// Build a Fancybox item for a single property document. Returns null for unsupported
// types so callers can fall through (e.g. direct download).
export function buildDocumentFancyboxItem(record) {
    if (!record) return null;
    var filename = record.filename || record.original_filename || '';
    var caption = record.original_filename || record.filename || '';
    var fileUrl = record.fileUrl || record.file_url || record.url || '';
    if (!fileUrl) return null;
    if (isPdfExtension(filename) || isPdfExtension(fileUrl)) {
        return buildPdfIframeItem(fileUrl, caption, record.thumbnailUrl || record.smallUrl || record.small_url || null);
    }
    if (isImageExtension(filename) || isImageExtension(fileUrl)) {
        var thumb = record.thumbnailUrl || record.smallUrl || record.small_url || fileUrl;
        return {
            src: fileUrl,
            type: 'image',
            opts: { caption: caption, thumb: thumb },
        };
    }
    return null;
}

// Forces iOS Safari to begin metadata fetch (HTTP Range) immediately on
// modal reveal so the user's first tap on the play control resolves play()
// instead of stalling on metadata-pending. We never call play() ourselves --
// the actual play gesture must come from the user to satisfy iOS Safari's
// autoplay restriction; this hook only warms up the media element.
function onVideoSlideShown(_instance, current) {
    if (!current || !current.$content) return;
    var videoEl = current.$content.find('video.fancybox-video-player').get(0);
    if (videoEl && typeof videoEl.load === 'function') {
        try { videoEl.load(); } catch (_e) { /* user can still tap play */ }
    }
}

// iOS Safari refuses to play <video> in modal overlays unless the element
// itself carries playsinline + webkit-playsinline. Fancybox v3's built-in
// type:'video' does not emit those attributes, so we render explicit HTML5
// markup via type:'html'.
function buildVideoItemHtml(url, posterUrl) {
    var safeUrl = escapeHtmlAttr(url);
    var safePoster = escapeHtmlAttr(posterUrl || '');
    var posterAttr = safePoster ? ' poster="' + safePoster + '"' : '';
    return ''
        + '<video class="fancybox-video-player" controls playsinline webkit-playsinline'
        + ' preload="metadata"' + posterAttr
        + ' style="display:block;max-width:100%;max-height:80vh;width:auto;height:auto;background:#000;">'
        + '<source src="' + safeUrl + '" type="video/mp4">'
        + '</video>';
}

export function buildFancyboxItems(photoRecords, videoTiles) {
    var items = [];

    if (Array.isArray(photoRecords)) {
        photoRecords.forEach(function (rec) {
            if (!rec || !rec.previewUrl) return;
            var caption = '';
            if (rec.file && rec.file.name) {
                caption = rec.file.name;
            } else if (rec.originalFilename) {
                caption = rec.originalFilename;
            }
            var thumb = rec.thumbnailUrl || rec.previewUrl;
            // viewerUrl = full-size; previewUrl = thumbnail.
            var src = rec.viewerUrl || rec.fullUrl || rec.previewUrl;
            items.push({
                src: src,
                type: 'image',
                opts: { caption: caption, thumb: thumb },
            });
        });
    }

    if (Array.isArray(videoTiles)) {
        videoTiles.forEach(function (tile) {
            if (!tile || tile.status !== 'ready' || !tile.url) return;
            var caption = tile.originalFilename || '';
            items.push({
                src: buildVideoItemHtml(tile.url, tile.thumbnailUrl),
                type: 'html',
                opts: {
                    caption: caption,
                    thumb: tile.thumbnailUrl || null,
                    slideClass: 'fancybox-video-slide',
                    afterShow: onVideoSlideShown,
                },
            });
        });
    }

    return items;
}

export function openPropertyGallery(items, startIndex, optionsOverride) {
    if (!Array.isArray(items) || items.length === 0) return false;
    if (typeof window === 'undefined') return false;
    var $ = window.$ || window.jQuery;
    if (!$ || !$.fancybox || typeof $.fancybox.open !== 'function') return false;

    var max = items.length - 1;
    var idx = parseInt(startIndex, 10);
    if (isNaN(idx) || idx < 0) idx = 0;
    if (idx > max) idx = max;

    var options = Object.assign({
        loop: false,
        animationEffect: 'fade',
        transitionEffect: 'fade',
        buttons: ['zoom', 'thumbs', 'fullScreen', 'close'],
        thumbs: { autoStart: false },
        video: { autoStart: false },
    }, optionsOverride || {});

    $.fancybox.open(items, options, idx);
    return true;
}
